/**
 * Main Application Entry Point
 * Cart system and Product system initialization
 */

import './styles/global/main.scss';

// Initialize cart system
import { initializeCart } from './scripts/cart/index.js';

// Initialize product system
import ProductSystem from './scripts/product/index.js';

// Optional: Import global components (uncomment to enable VideoSection and other optional components)
import './scripts/global/main.js';
import './scripts/global/video-grid.js';

// Import mini cart component
import './scripts/components/MiniCart.js';

const OCU_DRAWER_SELECTOR = '.ocu-cart-drawer';
const OCU_CART_LOADED_EVENT = 'ocu:cart:loaded';

const getOcuDrawer = () => document.querySelector(OCU_DRAWER_SELECTOR);

const isOcuDrawerContentReady = (drawer) => {
  if (!drawer) return false;

  const hasContent = drawer.textContent.trim().length > 20
    || Boolean(drawer.querySelector('form, button, a, input, img, svg, [role="button"], [class*="cart"]'));
  const isLoading = drawer.matches('[aria-busy="true"], .loading, .is-loading')
    || Boolean(drawer.querySelector('[aria-busy="true"], .loading, .is-loading, [data-loading="true"]'));

  return hasContent && !isLoading;
};

const initializeOcuCartDrawerCheck = () => {
  if (!document.body?.classList.contains('ocu-cart')) return;

  let observer = null;

  const markLoaded = () => {
    window.OCUCartLoaded = true;
    document.body.classList.add('ocu-cart--loaded');
    observer?.disconnect();
  };

  const handleOcuCartLoaded = () => {
    markLoaded();
  };

  addEventListener(OCU_CART_LOADED_EVENT, handleOcuCartLoaded, { once: true });

  // The early listener in theme.liquid records the event in case OCU loads
  // before this module initializes.
  if (window.OCUCartLoaded) {
    markLoaded();
    return;
  }

  const markLoadedFromDrawer = () => {
    const drawer = getOcuDrawer();
    if (!isOcuDrawerContentReady(drawer)) return false;

    markLoaded();
    return true;
  };

  if (markLoadedFromDrawer()) return;

  observer = new MutationObserver(() => {
    markLoadedFromDrawer();
  });

  observer.observe(document.documentElement, {
    attributes: true,
    characterData: true,
    childList: true,
    subtree: true
  });

  // Close the small gap between the initial check and observer attachment.
  markLoadedFromDrawer();
};

// Initialize both cart and product systems
const initializeSystems = async () => {
  console.log('🚀 [Main] Starting system initialization...');

  try {
    // Set up global cart redirect variable for A/B testing
    // This can be overridden by Intelligems or other A/B testing tools
    if (typeof window.cartRedirectToPage === 'undefined') {
      window.cartRedirectToPage = false; // Default: false = use drawer, true = redirect to /cart
    }
    
    console.log('🔧 [Main] Cart redirect setting:', window.cartRedirectToPage);
    console.log("TEST OCU CART")
    
    initializeOcuCartDrawerCheck();

    // Check header settings for cart functionality
    const cartContainer = document.querySelector('[data-cart-container]');
    const cartDrawerEnabled = cartContainer?.dataset.cartDrawerEnabled === 'true';
    const miniCartEnabled = cartContainer?.dataset.miniCartEnabled === 'true';
    
    console.log('🔧 [Main] Cart settings detected:', {
      cartDrawerEnabled,
      miniCartEnabled,
      cartRedirectToPage: window.cartRedirectToPage
    });

    // Initialize cart system with configuration
    console.log('🛒 [Main] Initializing cart system...');
    
    // Override cart drawer setting if global redirect variable is true
    const effectiveCartDrawerEnabled = window.cartRedirectToPage ? false : cartDrawerEnabled;
    
    const { cartManager, quantityManager, cartDrawer } = initializeCart({
      enableLogging: true,
      autoOpenDrawer: miniCartEnabled ? false : effectiveCartDrawerEnabled, // Disable drawer auto-open if mini cart is enabled
      apiTimeout: 10000, // 10 seconds timeout
      maxRetries: 5, // 5 retries
      debounceDelay: 300, // 300ms debounce for quantity updates
      notificationDuration: 4000, // 4 seconds for notifications
      enableCartDrawer: effectiveCartDrawerEnabled, // Pass setting to cart system
      enableMiniCart: miniCartEnabled, // Pass setting to cart system
      cartRedirectToPage: window.cartRedirectToPage // Pass global redirect setting
    });

    // Store references for global access if needed
    window.cartManager = cartManager;
    window.cartDrawer = cartDrawer;
    window.quantityManager = quantityManager;
    
    // Add global function for A/B testing tools to control cart redirect behavior
    window.setCartRedirectBehavior = (redirectToPage) => {
      window.cartRedirectToPage = redirectToPage;
      console.log('🔧 [Main] Cart redirect behavior updated:', redirectToPage);
    };

    console.log('✅ [Main] Cart system initialized successfully', {
      cartManager: !!cartManager,
      cartDrawer: !!cartDrawer,
      quantityManager: !!quantityManager,
      settings: { cartDrawerEnabled, miniCartEnabled }
    });

    // Initialize product system
    console.log('📦 [Main] Initializing product system...');
    const productSystem = new ProductSystem({
      enableLogging: true
    });

    // Store reference for global access
    window.productSystem = productSystem;

    console.log('✅ [Main] Product system initialized successfully', {
      productSystem: !!productSystem
    });

    console.log('🎉 [Main] All systems initialized successfully!');

  } catch (error) {
    console.error('❌ [Main] Failed to initialize systems:', error);
  }
};

// Start initialization process
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSystems);
} else {
  initializeSystems();
}
